import type { Behaviour } from "@hakili/contracts";
import { formatDate, formatMovement, t } from "@hakili/i18n";

/**
 * `S1-a` COMPORTEMENT DU COMPTE: **the pure view model of the tab**, the one place the served answer becomes words and figures (D-1020-7: every figure served, never computed; the architect's ruling of
 * 31 October: the window chip's months, years and count come from the contract's `window`, only the sentence frame lives in the catalogue, and the same for the hero's `{montant}`).
 *
 * What it decides, and nothing else: which of the three states the page is in (not yet read, read, partly read), the chip and the notice from the window, the hero (an undeclared recurring debit LEADS in the
 * server's own words, else the low point before the salary in the approved sentence, else nothing, never a made-up sentence), the lowest eve with its served percent, and the withheld sentence verbatim.
 * It draws NOTHING for what the contract does not serve (the sections « Ce qui sort », « Virements réguliers », « Incidents », their tiles and totals): there is no placeholder to produce.
 */

export interface BehaviourHero {
  readonly kind: "undeclared_recurring" | "low_point";
  readonly sentence: string;
}

/** One line of a statement, as the page shows it when a fact is traced: the statement's own wording, the amount, the running balance, where it was read (month, page). */
export interface BehaviourLine {
  readonly id: string;
  readonly date: string;
  readonly label: string;
  readonly amount: string;
  readonly balance: string | null;
  readonly where: string;
}

export interface BehaviourIncomeRow {
  readonly month: string;
  readonly total: string;
  readonly salary: string | null;
  readonly salaryDay: number | null;
}

export interface BehaviourLoanlike {
  readonly label: string;
  readonly amount: string;
  readonly day: number | null;
  readonly monthsSeen: number;
  readonly lineIds: readonly string[];
}

export interface BehaviourLowestEve {
  /** The statement line the fact was read from (`line_id`). */
  readonly lineIds: readonly string[];
  readonly date: string;
  readonly amount: string;
  /** « 16 % du salaire », from the served `{ percent }`; `null` when the salary is withheld. */
  readonly share: string | null;
  readonly others: readonly string[];
}

export type BehaviourView =
  | { readonly kind: "not_yet_read" }
  | {
      readonly kind: "read";
      readonly chip: string;
      /** Frame 208's notice: some month of the window could not be read. `null` when every month was. */
      readonly partialNotice: string | null;
      readonly hero: BehaviourHero | null;
      /** The server's sentence, verbatim, when the salary read on the statements does not reconcile. */
      readonly withheld: string | null;
      readonly lowestEve: BehaviourLowestEve | null;
      /** « Ce qui entre »: one row per month served; empty (no section) when none is. */
      readonly income: readonly BehaviourIncomeRow[];
      /** « Paiements récurrents qui ressemblent à un prêt »: the debits served as such; empty (no section) when none is. */
      readonly loanlike: readonly BehaviourLoanlike[];
      /** The statement lines a fact was read from, by id, as the answer carries them; an id it does not carry draws nothing. */
      readonly linesOf: (ids: readonly string[]) => readonly BehaviourLine[];
    };

const fill = (key: Parameters<typeof t>[0], values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`{${name}}`, value), t(key));

/** `YYYY-MM` as served: the month's name and its year. `null` for what is not a month (nothing is guessed). */
function parseMonth(month: string): { readonly name: string; readonly year: string } | null {
  const found = /^(\d{4})-(0[1-9]|1[0-2])$/u.exec(month);
  if (found === null) return null;
  return { name: t(`bank.comportement.mois${found[2] ?? ""}`), year: found[1] ?? "" };
}

/** « juillet 2026 », « août et septembre 2026 », « novembre 2025 et janvier 2026 »: the months as served, the year stated once where it is common. */
function monthsLabel(months: readonly string[]): string {
  const parsed = months.map(parseMonth).filter((one) => one !== null);
  const sameYear = parsed.length > 0 && parsed.every((one) => one.year === parsed[0]?.year);
  const names = parsed.map((one) => (sameYear ? one.name : `${one.name} ${one.year}`));
  const joined =
    names.length <= 1
      ? (names[0] ?? "")
      : `${names.slice(0, -1).join(", ")} et ${names[names.length - 1] ?? ""}`;
  return sameYear ? `${joined} ${parsed[0]?.year ?? ""}` : joined;
}

const monthWithYear = (month: string): string => {
  const one = parseMonth(month);
  return one === null ? month : `${one.name} ${one.year}`;
};

/** « juillet à septembre 2026 » (the year said once where it is common), « novembre 2025 à janvier 2026 ». */
function rangeLabel(from: string, to: string): string {
  const start = parseMonth(from);
  const end = parseMonth(to);
  if (start === null || end === null) return `${from}, ${to}`;
  return start.year === end.year
    ? `${start.name} à ${end.name} ${end.year}`
    : `${start.name} ${start.year} à ${end.name} ${end.year}`;
}

function chipOf(behaviour: Behaviour): string {
  const { window } = behaviour;
  const lus = String(window.months_read.length);
  if (window.months_unread.length > 0) {
    return fill("bank.comportement.fenetrePartielle", {
      lus,
      total: String(window.months_read.length + window.months_unread.length),
      mois: monthsLabel(window.months_read),
    });
  }
  return fill("bank.comportement.fenetre", {
    lus,
    periode: rangeLabel(
      window.from ?? window.months_read[0] ?? "",
      window.to ?? window.months_read.at(-1) ?? "",
    ),
  });
}

/** The month's name alone, as the record words its notice (« Le relevé de juillet … Ce qui précède juillet … »): the year is the chip's. */
const monthName = (month: string): string => parseMonth(month)?.name ?? month;

/** « de juillet », « d'août » (the elision), « de juillet et août »: the served names joined, the preposition before the first. */
function deMois(months: readonly string[]): string {
  const names = months.map(monthName);
  const joined =
    names.length <= 1
      ? (names[0] ?? "")
      : `${names.slice(0, -1).join(", ")} et ${names[names.length - 1] ?? ""}`;
  return /^[aeiouéèh]/iu.test(joined) ? `d'${joined}` : `de ${joined}`;
}

/**
 * Frame 208's notice, from the SERVED window alone. « Ce qui précède {premier} n'est pas connu » is true only when every month unread comes BEFORE every month read (the unread months open the window);
 * an unread month after a read month leaves the earlier ones read, and the sentence is false: the short notice then says only that no month is estimated. The months are `YYYY-MM` ids, compared as ids.
 */
function partialNoticeOf(behaviour: Behaviour): string | null {
  const { window } = behaviour;
  const [first] = window.months_unread;
  if (first === undefined) return null;
  const firstRead = window.months_read[0];
  const opensTheWindow = firstRead === undefined || window.months_unread.every((one) => one < firstRead);
  const single = window.months_unread.length === 1;
  const key = single
    ? opensTheWindow
      ? "bank.comportement.partielNonLu"
      : "bank.comportement.partielNonLuCourt"
    : opensTheWindow
      ? "bank.comportement.partielNonLuPluriel"
      : "bank.comportement.partielNonLuPlurielCourt";
  return fill(key, {
    demois: deMois(window.months_unread),
    lus: String(window.months_read.length),
    premier: monthName(first),
  });
}

function heroOf(behaviour: Behaviour): BehaviourHero | null {
  const { focus, balance } = behaviour;
  if (focus === null) return null;
  if (focus.kind === "undeclared_recurring") {
    // The debit is the server's own sentence, verbatim: a focus served without words draws nothing, never a sentence made here.
    return focus.words === null ? null : { kind: "undeclared_recurring", sentence: focus.words };
  }
  // The low point before the salary is a fact LINKED TO THE SALARY: withheld with it.
  if (behaviour.salary_withheld !== null) return null;
  if (focus.words !== null) return { kind: "low_point", sentence: focus.words };
  if (balance.lowest_eve === null) return null;
  return {
    kind: "low_point",
    sentence: fill("bank.comportement.heroBas", { montant: formatMovement(balance.lowest_eve.amount) }),
  };
}

function lowestEveOf(behaviour: Behaviour): BehaviourLowestEve | null {
  const { lowest_eve: lowest, eve_of_salary: eves } = behaviour.balance;
  if (lowest === null || behaviour.salary_withheld !== null) return null;
  const served = eves.find((one) => one.line_id === lowest.line_id);
  return {
    lineIds: [lowest.line_id],
    date: lowest.date,
    amount: formatMovement(lowest.amount),
    share:
      served === undefined
        ? null
        : fill("bank.comportement.partDuSalaire", { percent: String(served.share_of_salary.percent) }),
    others: lowest.others.map((one) =>
      fill("bank.comportement.autresMois", {
        montant: formatMovement(one.amount),
        mois: parseMonth(one.month)?.name ?? one.month,
      }),
    ),
  };
}

/** A statement line's date (`YYYY-MM-DD`, served) in the catalogue's prose: « 27 juillet 2026 », the first as « 1er ». What is not a date is shown as served (nothing is guessed). */
function dayLabel(date: string): string {
  const found = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(date);
  if (found === null) return date;
  const instant = new Date(`${date}T12:00:00Z`);
  return Number.isNaN(instant.getTime()) ? date : formatDate(instant);
}

function linesOf(behaviour: Behaviour, ids: readonly string[]): readonly BehaviourLine[] {
  return ids.flatMap((id) => {
    const line = behaviour.statement_lines.find((one) => one.id === id);
    if (line === undefined) return [];
    const month = monthWithYear(line.statement_month);
    return [
      {
        id: line.id,
        date: dayLabel(line.date),
        label: line.label,
        amount: formatMovement(line.amount),
        balance: line.balance === null ? null : formatMovement(line.balance),
        where: line.page === null ? month : `${month} · page ${String(line.page)}`,
      },
    ];
  });
}

function incomeOf(behaviour: Behaviour): readonly BehaviourIncomeRow[] {
  return behaviour.income.total_by_month.map((total) => {
    const month = behaviour.income.by_month.find((one) => one.month === total.month);
    const salary = month?.salary_amount ?? null;
    return {
      month: monthWithYear(total.month),
      total: formatMovement(total.total_credits),
      salary: salary === null ? null : formatMovement(salary),
      salaryDay: month?.salary_day ?? null,
    };
  });
}

export function behaviourViewOf(behaviour: Behaviour): BehaviourView {
  if (behaviour.state === "not_yet_read") return { kind: "not_yet_read" };
  return {
    kind: "read",
    chip: chipOf(behaviour),
    partialNotice: partialNoticeOf(behaviour),
    hero: heroOf(behaviour),
    withheld: behaviour.salary_withheld,
    lowestEve: lowestEveOf(behaviour),
    income: incomeOf(behaviour),
    loanlike: behaviour.recurring.loanlike.map((one) => ({
      label: one.label,
      amount: formatMovement(one.amount),
      day: one.day,
      monthsSeen: one.months_seen,
      lineIds: one.line_ids,
    })),
    linesOf: (ids) => linesOf(behaviour, ids),
  };
}
